import Dexie, { type Table } from "dexie";

// Локальная база на планшете. Это не кэш, а полноценное хранилище:
// сотрудник заводит визит без интернета, и запись живёт здесь, пока не уедет.
//
// Все id — UUID, придуманные прямо тут. Поэтому запись, созданная офлайн,
// сразу имеет окончательный идентификатор и не конфликтует с чужими.

export type Point = {
  id: string; type: "pharmacy" | "dental" | "podolog" | "cosmetolog"; name: string;
  network?: string | null; ownership?: "state" | "private" | null;
  contractorId?: string | null;
  city: string; district?: string | null; address: string;
  category: "A" | "B" | "C"; status: string;
  lat?: number | null; lon?: number | null; note?: string | null;
  /** Из списка какого бренда точка пришла и кто её завёл — на этом держатся «мои точки». */
  brandId?: string | null; createdById?: string | null;
  updatedAt?: string; deletedAt?: string | null;
};

export type Contact = {
  id: string; pointId: string; fullName: string; position: string;
  phone?: string | null; email?: string | null;
  consent: boolean; consentAt?: string | null; note?: string | null;
  updatedAt?: string; deletedAt?: string | null;
};

export type VisitItem = { productId: string; qty: number };
export type ShelfCheck = { productId: string; onShelf: boolean; note?: string | null };

/** Фото, которое уже лежит на сервере. Приходит вместе с визитом при обмене
 *  и нужно, чтобы устройство знало про снимки, сделанные на другом. */
export type RemotePhoto = { id: string; visitId: string; file: string };

export type Visit = {
  id: string; pointId: string; userId?: string; visitedAt: string; summary: string;
  contactId?: string | null; lat?: number | null; lon?: number | null;
  geoAccuracy?: number | null; agreement?: string | null; nextVisitOn?: string | null;
  posOk?: boolean | null; shelfNote?: string | null; needsReview?: boolean;
  items: VisitItem[]; shelfChecks: ShelfCheck[]; photos?: RemotePhoto[];
  updatedAt?: string; deletedAt?: string | null;
};

export type Product = {
  id: string; brandId: string; name: string; article?: string | null;
  /** Линейка внутри бренда — по ней группируется чек-лист выкладки. */
  line?: string | null;
  kind: string; sortOrder: number; active: boolean; deletedAt?: string | null;
};

export type Contractor = { id: string; name: string; unp?: string | null; deletedAt?: string | null };
export type Brand = { id: string; name: string; active: boolean; deletedAt?: string | null };
export type MatrixRow = { id: string; scope: string; scopeId: string; productId: string; deletedAt?: string | null };
export type StockMove = { id: string; userId: string; productId: string; qty: number; reason: string; visitId?: string | null; createdAt?: string; deletedAt?: string | null };

/** Очередь на отправку. Храним только «что изменилось», а не снимок:
 *  при отправке читаем актуальную запись, поэтому правки не теряются. */
export type Outbox = { key: string; kind: "point" | "contact" | "visit"; entityId: string; at: string };

/** Фото ждут своей очереди отдельно — они тяжёлые и грузятся по одному. */
export type LocalPhoto = { id: string; visitId: string; blob: Blob; at: string; uploaded: 0 | 1 };

export type Meta = { key: string; value: unknown };

class CrmDb extends Dexie {
  points!: Table<Point, string>;
  contacts!: Table<Contact, string>;
  visits!: Table<Visit, string>;
  products!: Table<Product, string>;
  contractors!: Table<Contractor, string>;
  brands!: Table<Brand, string>;
  matrix!: Table<MatrixRow, string>;
  stockMoves!: Table<StockMove, string>;
  outbox!: Table<Outbox, string>;
  photos!: Table<LocalPhoto, string>;
  meta!: Table<Meta, string>;

  constructor() {
    super("lacalut-crm");
    this.version(1).stores({
      points: "id, type, city, contractorId, name",
      contacts: "id, pointId",
      visits: "id, pointId, visitedAt",
      products: "id, brandId, article, sortOrder",
      contractors: "id, name",
      brands: "id",
      matrix: "id, [scope+scopeId], productId",
      stockMoves: "id, productId, visitId",
      outbox: "key, kind, entityId",
      photos: "id, visitId, uploaded",
      meta: "key",
    });
  }
}

export const db = new CrmDb();

export const uuid = () => crypto.randomUUID();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row ? (row.value as T) : fallback;
}
export const setMeta = (key: string, value: unknown) => db.meta.put({ key, value });

/** Ставит запись в очередь на отправку. Повторный вызов не плодит дубли. */
export const queue = (kind: Outbox["kind"], entityId: string) =>
  db.outbox.put({ key: kind + ":" + entityId, kind, entityId, at: new Date().toISOString() });

/** Полная очистка — при отзыве доступа и при выходе. */
export async function wipe() {
  await Promise.all([
    db.points.clear(), db.contacts.clear(), db.visits.clear(), db.products.clear(),
    db.contractors.clear(), db.brands.clear(), db.matrix.clear(), db.stockMoves.clear(),
    db.outbox.clear(), db.photos.clear(), db.meta.clear(),
  ]);
}
